import { z } from 'zod';
import { contactNameSchema, contactPhoneSchema } from './common';
import { type AddressMeta, verifiedAddressMetaSchema } from './address';
import { MAX_ROUTE_REQUESTS, routeCargoLabel } from './vehicle-routes';
import { WORK_TIME_MESSAGE, isWithinWorkTimeAt } from './time';

// ── Ездка заявки (план `docs/route-trips-plan.md`, Р1–Р3, Р13а) ──
//
// Ездка — строка **заявки**, а не маршрута (Р1): своя пара адресов с метаданными, своё
// количество, свои контакты на обоих концах, своё время подачи. Одна заявка — одна или несколько
// ездок, у ездки ровно одна заявка.
//
// Почему адреса и количество переехали сюда с заявки (Р2): держать их в двух местах нельзя — у
// заявки с ездками `A→B` и `A→C` «адреса разгрузки заявки» не существует, и любое поле заявки на
// этот вопрос отвечало бы наугад. У заявки остаётся то, что описывает заказ целиком: заказчик,
// тип и категория ТС, время первой подачи, комментарий, файлы.
//
// Что ездка **не** знает: своего объекта затрат (Р25) — он наследуется от заявки, иначе
// двойственность «объект или отдел» переезжает внутрь заявки; и своего места в маршруте — оно
// приходит раскладкой (`placement`), потому что порядок объезда принадлежит рейсу, а не заказу.
//
// Этап 1 аддитивен: эти контракты живут **рядом** с действующими полями заявки
// (`loadingLocation`, `volumeM3` и прочими), ничего не переключая. Переключение — этапом 2.

/**
 * Ездка заявки в том виде, в каком её отдаёт сервер.
 *
 * Мягко удалённые ездки (Р13а: `deleted_at`) в DTO **не приходят вовсе** — ни в карточку, ни в
 * раскладку, ни в итог. Видны они только из журнала листов и из истории, где и должны быть:
 * бланк строгой отчётности обязан помнить, что именно печаталось, а карточка заявки показывает
 * то, что едет сейчас.
 */
export interface VehicleRequestTripDto {
  id: string;
  /**
   * Номер ездки внутри заявки. Неизменяем и **не переиспользуется** (Р13а): после мягкого
   * удаления следующая ездка получает следующий свободный, иначе «ТС-40/2» в старом листе и
   * «ТС-40/2» в новом означали бы разное.
   */
  num: number;
  /** «ТС-40/2» — номер заявки и номер ездки в ней; собран сервером, портал его не склеивает. */
  displayNumber: string;
  fromLocation: string;
  toLocation: string;
  /**
   * Метаданные адреса (ADR 0006): чем адрес опознан — ФИАС, гео, запись справочника. `null` —
   * только у строк, доехавших бэкфилом от заявок, заведённых до жёсткой модели: на запись схема
   * непроверенный адрес не пропускает.
   */
  fromAddress: AddressMeta | null;
  toAddress: AddressMeta | null;
  /**
   * What the trip carries, in the requester's words: "sand", "PK 60-15 slabs" (ADR 0239). Empty
   * only for trips created before migration 0397; a new value is never empty (`cargoNameSchema`).
   */
  cargoName: string;
  /**
   * Количество **этой** ездки. Оба поля пусты у легкового: груза там не бывает вовсе
   * (`isCargoAmountRequired('leg3') === false`), и требовать число значило бы заставлять
   * заявителя его выдумывать.
   */
  volumeM3: number | null;
  weightTons: number | null;
  /** Контакт на погрузке: кто отдаёт груз и по какому телефону его искать на месте. */
  fromResponsibleName: string;
  fromResponsiblePhone: string;
  /** Контакт на разгрузке: он же открывает ворота на приёмке. */
  toResponsibleName: string;
  toResponsiblePhone: string;
  /**
   * Своё время подачи; `null` — время заявки (Р3). Уточняющее, а не главное: датой рейса,
   * фильтрами и рабочим окном заведует `scheduledAt` **заявки**, а ездка отвечает на «во сколько
   * именно эта» — шесть ездок за смену едут по графику, а не одновременно.
   */
  scheduledAt: string | null;
  /** Примечание к ездке: «песок, звонить за час». В бланке отбрасывается первым (Р11а). */
  comment: string;
  /**
   * Где ездка стоит в маршруте: позиции точек погрузки и разгрузки в порядке объезда. `null` —
   * не разложена: заявка ещё не в рейсе либо её вынули. Позиции, а не идентификаторы точек:
   * карточке заявки нужно «где по порядку», а состав точки — вопрос карточки маршрута.
   */
  placement: { loadPosition: number; unloadPosition: number } | null;
}

/**
 * Груз ездки в том виде, в каком его печатает бланк: объём, а если его нет — масса.
 *
 * Тонкая обёртка над `routeCargoLabel` — правило одно на портал и на бумагу, и второй его
 * записи существовать не должно: разойдясь в единицах, карточка и лист заспорят о том, что
 * везли. Здесь только форма аргумента другая: у ездки количество приходит парой полей DTO.
 *
 * Тем же вызовом печатается и итог по заявке — `requestCargoTotal` отдаёт структуру этой же
 * формы, чтобы «60 м³» в шапке считалось тем же правилом, что «12 м³» в строке.
 */
export function tripCargoLabel(t: { volumeM3: number | null; weightTons: number | null }): string {
  return routeCargoLabel(t.volumeM3, t.weightTons);
}

/**
 * Точность количества — `numeric(12,3)` в базе (§5.1). Складывать такие числа как есть нельзя:
 * `0.1 + 0.2` в двоичной плавающей точке даёт `0.30000000000000004`, и на шести ездках итог
 * заявки показал бы хвост из нулей там, где человек ждёт «60 м³». Поэтому сумма приводится к
 * шкале хранения — тем же приёмом, что и объём по составу техники в вывозе мусора
 * (`sumVehicleVolume`, `packages/contracts/src/waste-requests.ts`).
 */
const AMOUNT_SCALE = 1000;

function toAmountScale(value: number): number {
  return Math.round(value * AMOUNT_SCALE) / AMOUNT_SCALE;
}

/**
 * Итог по заявке: «60 м³ · 6 ездок».
 *
 * Складываются **живые** ездки, и подпись аргумента говорит об этом прямо: мягко удалённые
 * (Р13а) в DTO не приходят вовсе, поэтому фильтровать здесь нечего — но тот, кто соберёт список
 * сам, из строк базы, обязан отбросить `deleted_at` до вызова. Иначе в ёмкость и в итог попадёт
 * ездка, которая никуда не едет и на бумаге осталась только историей.
 *
 * `null` в поле — количества такого рода нет ни у одной ездки: у легкового его не бывает вовсе
 * (`isCargoAmountRequired`), а смешанная заявка «часть в кубах, часть в тоннах» законна, и итог
 * у неё двойной. Ноль на месте `null` был бы утверждением «везём нисколько».
 *
 * Форма результата совпадает с аргументом `tripCargoLabel` намеренно: подпись итога собирается
 * тем же правилом, что подпись строки.
 */
export function requestCargoTotal(liveTrips: readonly VehicleRequestTripDto[]): {
  volumeM3: number | null;
  weightTons: number | null;
  trips: number;
} {
  let volumeM3: number | null = null;
  let weightTons: number | null = null;
  for (const trip of liveTrips) {
    if (trip.volumeM3 !== null) volumeM3 = (volumeM3 ?? 0) + trip.volumeM3;
    if (trip.weightTons !== null) weightTons = (weightTons ?? 0) + trip.weightTons;
  }
  return {
    volumeM3: volumeM3 === null ? null : toAmountScale(volumeM3),
    weightTons: weightTons === null ? null : toAmountScale(weightTons),
    trips: liveTrips.length,
  };
}

// ── Схема ездки ──
//
// Границы полей взяты у действующих полей заявки (`vehicle-requests.ts`) и повторены здесь
// значение в значение: адрес до 1000 знаков, комментарий до 2000, количество — положительное с
// тремя знаками после запятой (`numeric(12,3)`). Копия, а не импорт, по одной причине: там эти
// подсхемы приватные (`locationSchema`, `commentSchema`, `amountSchema` не экспортируются), а
// чужой файл этап 1 не трогает. Место им — общий модуль, и свести их туда следует этапом 2,
// когда те же поля уйдут с заявки на ездку: две копии границ переживут ровно один релиз.

/** Адрес строкой: то, что печатается в бланке и читается водителем. */
const tripLocationSchema = z.string().trim().min(1).max(1000);

/** Примечание к ездке — той же длины, что комментарий заявки. */
const tripCommentSchema = z.string().trim().max(2000);

export const CARGO_NAME_MESSAGE = 'Укажите, что везём';

/**
 * The stored form of "what the trip carries": empty is legal only as the state of a trip older
 * than migration 0397. An edit row keeps it (R2a) — the server compares it with the stored value.
 */
export const storedCargoNameSchema = z.string().trim().max(200);

/**
 * "What we carry" as a new value (ADR 0239): free text, required for every freight trip, a
 * passenger car included — there the answer is "passengers" or "documents", not an invented
 * number, so the exemption of `isCargoAmountRequired` does not apply.
 */
export const cargoNameSchema = storedCargoNameSchema.min(1, CARGO_NAME_MESSAGE);

/**
 * The refusal of a NEW trip that does not carry the key at all. The current portal always sends it,
 * empty or not; only a tab opened before ADR 0239 omits it, and the generic "validation error: cargoName"
 * would tell its user nothing — this text says what to do.
 */
export const CARGO_NAME_STALE_TAB_MESSAGE =
  'Портал обновился: у ездки появилось обязательное поле «Что везём», а эта вкладка его не знает. Обновите страницу (Ctrl+R) — заполненное останется в черновике — и укажите, что везём';

/**
 * The first trip of a new command without the `cargoName` key, or -1. Not a schema rule: a body
 * sent before ADR 0239 must still pass the stable schema so that its receipt can be replayed and
 * reconciled (`createVehicleRequestStableSchema`) — the requirement applies to new commands only.
 */
export function firstTripWithoutCargoName(trips: readonly { cargoName?: string }[]): number {
  return trips.findIndex((trip) => trip.cargoName === undefined);
}

/** Положительное количество с не более чем 3 знаками после запятой (`numeric(12,3)`). */
const tripAmountSchema = z
  .number()
  .positive('Значение должно быть больше 0')
  .max(999_999_999.999, 'Слишком большое значение')
  .refine(
    (v) => Math.abs(v * AMOUNT_SCALE - Math.round(v * AMOUNT_SCALE)) < 1e-6,
    'Не более 3 знаков после запятой',
  );

/**
 * Своё время подачи ездки: ISO 8601 с обязательным offset, как у заявки.
 *
 * Рабочее окно проверяется здесь же, а не сервером: у заявки этот вопрос решает пара
 * `scheduledAt` + `scheduledTimeUnspecified` (окно не проверяется, когда время не задано), а у
 * ездки «не задано» выражено самим `null` — значит присланное время задано всегда, и спрашивать
 * не о чем. Не проверять его вовсе было бы расхождением: 03:00 у заявки схема не принимает, а у
 * ездки, которая и есть выезд, приняла бы.
 *
 * Прошлое схема здесь не разбирает: день заказа принадлежит заявке (Р3), и коррекция задним
 * числом (ADR 0101) спрашивается один раз — у неё.
 */
const tripScheduledAtSchema = z
  .string()
  .datetime({ offset: true })
  .refine((v) => isWithinWorkTimeAt(new Date(v)), WORK_TIME_MESSAGE);

/**
 * Ездка в теле запроса.
 *
 * Чего здесь нет и почему:
 *
 * - **`num`** — номер назначает сервер. Принять его от клиента значило бы отдать наружу
 *   `UNIQUE (request_id, num)` и правило «номер не переиспользуется» (Р13а): форма, не знающая
 *   про мягко удалённые ездки, честно прислала бы «2» на место снесённой второй, и «ТС-40/2» в
 *   выданном листе перестал бы означать ту ездку, что напечатана;
 * - **`id`** — этой схемой ездка **заводится**. Правка списка ездок (`PATCH /vehicle-requests/:id`,
 *   §7) отличает существующую строку от новой, и её схема получается расширением этой
 *   (`requestTripSchema.extend({ id: uuidSchema.optional() })`) — чтобы границы полей не
 *   разъехались между заведением и правкой;
 * - **CHECK «объём или масса»** — его нет ни здесь, ни в базе (§5.1) намеренно: нужен ли груз,
 *   зависит от бланка заказанного типа ТС (`isCargoAmountRequired`), а бланк живёт в
 *   справочнике — схеме его взять неоткуда. Обязательность остаётся условной и серверной
 *   (`assertCargoAmount`), как она и задумана для заявки.
 *
 * Адрес приходит **парой** — строка и метаданные (жёсткая модель ADR 0006): оба поля
 * обязательны, поэтому пара держится самой формой схемы, и отдельная сверка «переданы вместе»
 * нужна только там, где поля необязательны (`updateVehicleRequestSchema`). Верификацию адреса
 * целиком берёт на себя `verifiedAddressMetaSchema`: выбран из подсказок DaData либо из
 * справочника — свободный ввод на запись не проходит.
 */
export const requestTripSchema = z
  .object({
    fromLocation: tripLocationSchema,
    toLocation: tripLocationSchema,
    fromAddress: verifiedAddressMetaSchema,
    toAddress: verifiedAddressMetaSchema,
    /**
     * Optional as a key, never empty as a value (ADR 0239). A body sent before the field must stay
     * valid for a receipt replay and reconciliation, with the fingerprint it was stored under; a
     * new command without the key is refused by the server (`firstTripWithoutCargoName`).
     */
    cargoName: cargoNameSchema.optional(),
    volumeM3: tripAmountSchema.nullable().optional(),
    weightTons: tripAmountSchema.nullable().optional(),
    /**
     * Контакт на каждом конце, а не один на ездку: грузят и принимают разные люди в разных
     * местах, и водителю нужен тот, кто откроет ворота именно здесь. Обязательны оба — рейс без
     * контакта на разгрузке заканчивается простоем у закрытой площадки. Тем же требованием
     * контакты стоят у действующей заявки; пустая строка в базе (§5.1 `DEFAULT ''`) —
     * состояние бэкфила, а не то, что принимается на запись.
     */
    fromResponsibleName: contactNameSchema,
    fromResponsiblePhone: contactPhoneSchema,
    toResponsibleName: contactNameSchema,
    toResponsiblePhone: contactPhoneSchema,
    /** Своё время подачи; не прислано или `null` — «как у заявки» (Р3). */
    scheduledAt: tripScheduledAtSchema.nullable().optional(),
    comment: tripCommentSchema.optional().default(''),
  })
  .strict();
export type RequestTripInput = z.infer<typeof requestTripSchema>;

/**
 * Ездок в заявке не меньше одной: заявка без ездки — заказ, в котором не сказано, что везти и
 * куда.
 *
 * Верхняя граница здесь грубая — потолок по всем бланкам (`MAX_ROUTE_REQUESTS`), тот же, которым
 * ограничены списки состава и перестановки. Настоящую ёмкость знает только сервер: строк задания
 * у 4-П семь, у формы № 3 десять (`ROUTE_REQUEST_CAPACITY`), считаются они по **всему** составу
 * рейса вместе с линейными днями, а бланк лежит в справочнике типов ТС — схеме его взять
 * неоткуда. Но и вовсе без границы оставлять нельзя: заявка едет одним маршрутом целиком (Р7),
 * значит её ездок физически не может быть больше, чем строк в самом просторном бланке, а
 * неограниченный массив в теле запроса — это разбор мегабайта адресов до первой проверки.
 */
export const requestTripsSchema = z
  .array(requestTripSchema)
  .min(1, 'Добавьте хотя бы одну ездку')
  .max(MAX_ROUTE_REQUESTS, `Ездок в заявке не больше ${MAX_ROUTE_REQUESTS}`);
